import { ComponentType } from "@/types";
import { ChartDataPoint } from "../ChartDataManager";

export interface ChartTemplate {
  id: string;
  name: string;
  description: string;
  type: ComponentType;
  category: "business" | "agricultural" | "financial" | "analytics" | "scientific";
  properties: Record<string, any>;
  data: ChartDataPoint[];
  keywords: string[]; // AI keywords to trigger this template
}

// Business templates
const businessTemplates: ChartTemplate[] = [
  {
    id: "sales-dashboard",
    name: "Sales Dashboard",
    description: "Monthly sales performance tracking",
    type: ComponentType.LINE_CHART,
    category: "business",
    properties: {
      title: "Monthly Sales Performance",
      description: "Revenue and growth tracking",
      showLegend: true,
      showGrid: true,
      showTooltip: true,
      colors: ["#3B82F6", "#10B981", "#F59E0B"],
      lineKeys: ["revenue", "profit", "growth"],
      strokeWidth: 2,
      showDots: true,
      xAxisLabel: "Month",
      yAxisLabel: "Amount ($)",
    },
    data: [
      { name: "Jan", revenue: 45000, profit: 12000, growth: 5 },
      { name: "Feb", revenue: 52000, profit: 15000, growth: 8 },
      { name: "Mar", revenue: 48000, profit: 13500, growth: 6 },
      { name: "Apr", revenue: 61000, profit: 18000, growth: 12 },
      { name: "May", revenue: 55000, profit: 16500, growth: 9 },
      { name: "Jun", revenue: 67000, profit: 21000, growth: 15 },
    ],
    keywords: ["sales", "revenue", "business", "performance", "dashboard"],
  },
  {
    id: "market-share",
    name: "Market Share",
    description: "Company market share distribution",
    type: ComponentType.PIE_CHART,
    category: "business",
    properties: {
      title: "Market Share Distribution",
      showLegend: true,
      showLabels: true,
      colors: ["#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#3B82F6"],
      innerRadius: 0,
      outerRadius: 80,
    },
    data: [
      { name: "Our Company", value: 35 },
      { name: "Competitor A", value: 25 },
      { name: "Competitor B", value: 20 },
      { name: "Competitor C", value: 15 },
      { name: "Others", value: 5 },
    ],
    keywords: ["market", "share", "distribution", "competition", "percentage"],
  },
  {
    id: "quarterly-comparison",
    name: "Quarterly Comparison",
    description: "Quarter-over-quarter performance",
    type: ComponentType.BAR_CHART,
    category: "business",
    properties: {
      title: "Quarterly Performance Comparison",
      showLegend: true,
      showGrid: true,
      colors: ["#3B82F6", "#10B981"],
      xAxisLabel: "Quarter",
      yAxisLabel: "Revenue ($K)",
    },
    data: [
      { name: "Q1", current: 150, previous: 120 },
      { name: "Q2", current: 180, previous: 150 },
      { name: "Q3", current: 165, previous: 180 },
      { name: "Q4", current: 210, previous: 165 },
    ],
    keywords: ["quarterly", "quarter", "comparison", "q1", "q2", "q3", "q4"],
  },
];

// Agricultural templates
const agriculturalTemplates: ChartTemplate[] = [
  {
    id: "crop-yield",
    name: "Crop Yield Tracker",
    description: "Monthly crop yield and rainfall correlation",
    type: ComponentType.LINE_CHART,
    category: "agricultural",
    properties: {
      title: "Crop Yield Analysis",
      description: "Yield vs Rainfall Correlation",
      showLegend: true,
      showGrid: true,
      colors: ["#8BC34A", "#2196F3"],
      lineKeys: ["yield", "rainfall"],
      strokeWidth: 2,
      showDots: true,
      xAxisLabel: "Month",
      yAxisLabel: "Yield (tons) / Rainfall (mm)",
    },
    data: [
      { name: "Jan", yield: 45, rainfall: 120 },
      { name: "Feb", yield: 42, rainfall: 95 },
      { name: "Mar", yield: 50, rainfall: 140 },
      { name: "Apr", yield: 65, rainfall: 180 },
      { name: "May", yield: 72, rainfall: 200 },
      { name: "Jun", yield: 68, rainfall: 160 },
      { name: "Jul", yield: 55, rainfall: 110 },
      { name: "Aug", yield: 48, rainfall: 85 },
      { name: "Sep", yield: 52, rainfall: 105 },
      { name: "Oct", yield: 60, rainfall: 150 },
      { name: "Nov", yield: 58, rainfall: 135 },
      { name: "Dec", yield: 50, rainfall: 125 },
    ],
    keywords: ["crop", "yield", "harvest", "rainfall", "agriculture", "farming"],
  },
  {
    id: "soil-moisture",
    name: "Soil Moisture Monitoring",
    description: "Daily soil moisture levels across fields",
    type: ComponentType.AREA_CHART,
    category: "agricultural",
    properties: {
      title: "Soil Moisture Levels",
      description: "Field moisture monitoring",
      showLegend: true,
      showGrid: true,
      colors: ["#795548", "#8D6E63", "#A1887F"],
      fillOpacity: 0.6,
      xAxisLabel: "Day",
      yAxisLabel: "Moisture (%)",
    },
    data: [
      { name: "Day 1", fieldA: 65, fieldB: 70, fieldC: 62 },
      { name: "Day 2", fieldA: 63, fieldB: 68, fieldC: 60 },
      { name: "Day 3", fieldA: 60, fieldB: 65, fieldC: 58 },
      { name: "Day 4", fieldA: 58, fieldB: 62, fieldC: 55 },
      { name: "Day 5", fieldA: 72, fieldB: 75, fieldC: 70 },
      { name: "Day 6", fieldA: 70, fieldB: 73, fieldC: 68 },
      { name: "Day 7", fieldA: 68, fieldB: 71, fieldC: 66 },
    ],
    keywords: ["soil", "moisture", "water", "irrigation", "field", "monitoring"],
  },
  {
    id: "livestock-distribution",
    name: "Livestock Distribution",
    description: "Farm animal population breakdown",
    type: ComponentType.PIE_CHART,
    category: "agricultural",
    properties: {
      title: "Livestock Distribution",
      showLegend: true,
      showLabels: true,
      colors: ["#795548", "#8BC34A", "#FF9800", "#607D8B", "#E91E63"],
    },
    data: [
      { name: "Cattle", value: 150 },
      { name: "Sheep", value: 300 },
      { name: "Goats", value: 120 },
      { name: "Chickens", value: 500 },
      { name: "Pigs", value: 80 },
    ],
    keywords: ["livestock", "cattle", "sheep", "animals", "farm", "distribution"],
  },
  {
    id: "irrigation-schedule",
    name: "Irrigation Schedule",
    description: "Weekly water usage by field",
    type: ComponentType.BAR_CHART,
    category: "agricultural",
    properties: {
      title: "Weekly Irrigation Schedule",
      description: "Water usage per field (liters)",
      showLegend: true,
      showGrid: true,
      colors: ["#2196F3", "#03A9F4", "#00BCD4"],
      stacked: true,
      xAxisLabel: "Day",
      yAxisLabel: "Water (liters)",
    },
    data: [
      { name: "Mon", fieldA: 1000, fieldB: 800, fieldC: 1200 },
      { name: "Tue", fieldA: 0, fieldB: 1000, fieldC: 900 },
      { name: "Wed", fieldA: 1200, fieldB: 0, fieldC: 1100 },
      { name: "Thu", fieldA: 900, fieldB: 950, fieldC: 0 },
      { name: "Fri", fieldA: 0, fieldB: 1100, fieldC: 1000 },
      { name: "Sat", fieldA: 1100, fieldB: 0, fieldC: 1150 },
      { name: "Sun", fieldA: 800, fieldB: 900, fieldC: 0 },
    ],
    keywords: ["irrigation", "water", "schedule", "watering", "field"],
  },
];

// Financial templates
const financialTemplates: ChartTemplate[] = [
  {
    id: "portfolio-performance",
    name: "Portfolio Performance",
    description: "Investment portfolio tracking",
    type: ComponentType.LINE_CHART,
    category: "financial",
    properties: {
      title: "Portfolio Performance",
      description: "YTD Returns",
      showLegend: true,
      showGrid: true,
      colors: ["#10B981", "#EF4444", "#3B82F6"],
      lineKeys: ["portfolio", "benchmark", "target"],
      strokeWidth: 2,
      area: true,
      areaOpacity: 0.2,
      xAxisLabel: "Month",
      yAxisLabel: "Return (%)",
    },
    data: [
      { name: "Jan", portfolio: 2.5, benchmark: 2.1, target: 2.0 },
      { name: "Feb", portfolio: 3.8, benchmark: 3.2, target: 3.0 },
      { name: "Mar", portfolio: 3.2, benchmark: 3.5, target: 3.0 },
      { name: "Apr", portfolio: 5.1, benchmark: 4.8, target: 4.0 },
      { name: "May", portfolio: 6.2, benchmark: 5.5, target: 5.0 },
      { name: "Jun", portfolio: 7.8, benchmark: 6.9, target: 6.0 },
    ],
    keywords: ["portfolio", "investment", "returns", "financial", "performance"],
  },
  {
    id: "expense-breakdown",
    name: "Expense Breakdown",
    description: "Monthly expense categories",
    type: ComponentType.PIE_CHART,
    category: "financial",
    properties: {
      title: "Monthly Expense Breakdown",
      showLegend: true,
      showLabels: true,
      innerRadius: 40,
      outerRadius: 80,
      colors: ["#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"],
    },
    data: [
      { name: "Rent", value: 1500 },
      { name: "Food", value: 600 },
      { name: "Transport", value: 300 },
      { name: "Utilities", value: 200 },
      { name: "Entertainment", value: 250 },
      { name: "Savings", value: 800 },
    ],
    keywords: ["expense", "budget", "spending", "cost", "financial"],
  },
];

// All templates combined
export const chartTemplates: ChartTemplate[] = [
  ...businessTemplates,
  ...agriculturalTemplates,
  ...financialTemplates,
];

/**
 * Template manager for chart templates
 */
export class ChartTemplateManager {
  private static instance: ChartTemplateManager;

  static getInstance(): ChartTemplateManager {
    if (!ChartTemplateManager.instance) {
      ChartTemplateManager.instance = new ChartTemplateManager();
    }
    return ChartTemplateManager.instance;
  }

  /**
   * Find template by keywords in command
   */
  findTemplateByCommand(command: string): ChartTemplate | null {
    const lowerCommand = command.toLowerCase();

    // Find best matching template based on keyword matches
    let bestMatch: ChartTemplate | null = null;
    let bestScore = 0;

    for (const template of chartTemplates) {
      let score = 0;

      // Check template name
      if (lowerCommand.includes(template.name.toLowerCase())) {
        score += 10;
      }

      // Check keywords
      for (const keyword of template.keywords) {
        if (lowerCommand.includes(keyword)) {
          score += 2;
        }
      }

      // Check category
      if (lowerCommand.includes(template.category)) {
        score += 3;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = template;
      }
    }

    return bestScore >= 4 ? bestMatch : null;
  }

  /**
   * Get templates by category
   */
  getTemplatesByCategory(category: string): ChartTemplate[] {
    return chartTemplates.filter(t => t.category === category);
  }

  /**
   * Get template by ID
   */
  getTemplateById(id: string): ChartTemplate | undefined {
    return chartTemplates.find(t => t.id === id);
  }

  /**
   * Apply template to create chart configuration
   */
  applyTemplate(template: ChartTemplate): { type: ComponentType; properties: any; data: any } {
    return {
      type: template.type,
      properties: { ...template.properties },
      data: [...template.data],
    };
  }

  /**
   * Get template suggestions based on context
   */
  getSuggestions(context?: { isAgricultural?: boolean; category?: string }): string[] {
    const suggestions: string[] = [];

    if (context?.isAgricultural) {
      suggestions.push(
        "Create crop yield chart",
        "Show soil moisture levels",
        "Display livestock distribution",
        "Track irrigation schedule"
      );
    } else if (context?.category === "business") {
      suggestions.push(
        "Create sales dashboard",
        "Show market share",
        "Display quarterly comparison"
      );
    } else if (context?.category === "financial") {
      suggestions.push(
        "Show portfolio performance",
        "Display expense breakdown"
      );
    } else {
      // General suggestions
      suggestions.push(
        "Create a chart from template",
        "Use agricultural template",
        "Apply business dashboard",
        "Show sample data"
      );
    }

    return suggestions;
  }
}

export const chartTemplateManager = ChartTemplateManager.getInstance();
